import {
  getPatientImages,
  getMultiplePatientsImages,
} from "../../../../../services/api-clinic";
import type { TSession } from "../../../../../utils/types";

const PROFILE_VIEW_TYPE_PRIORITY = [
  "HEAD_FRONT",
  "HEAD_LEFT",
  "HEAD_RIGHT",
  "HEAD_LEFT_45",
  "HEAD_RIGHT_45",
] as const;

type PatientWithProfileImage = {
  patientRecordId?: string;
  clinicOSUUID?: string;
  profileImageUrl?: string | null;
  [key: string]: unknown;
};

const inFlightProfileImageRequests = new Map<string, Promise<string | null>>();

const getRequestKey = (clinicId: string | null, patientId: string) =>
  `${clinicId || ""}:${patientId}`;

const getViewPriority = (viewType: string | null): number => {
  const index = PROFILE_VIEW_TYPE_PRIORITY.findIndex(
    (view) => view === viewType,
  );
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const pickBestProfileImageUrl = (
  images: Awaited<ReturnType<typeof getPatientImages>>,
): string | null => {
  if (!Array.isArray(images) || images.length === 0) {
    return null;
  }

  const sorted = [...images].sort((a, b) => {
    const aPriority = getViewPriority(a.image_data.view_type);
    const bPriority = getViewPriority(b.image_data.view_type);

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    if (Boolean(a.image_data.is_starred) !== Boolean(b.image_data.is_starred)) {
      return a.image_data.is_starred ? -1 : 1;
    }

    const aCreatedAt = new Date(a.image_data.created_at || 0).getTime();
    const bCreatedAt = new Date(b.image_data.created_at || 0).getTime();
    return bCreatedAt - aCreatedAt;
  });

  return sorted[0]?.presigned_urls?.[0] || null;
};

export const getPatientProfileImageUrl = async (
  session: TSession | null,
  patientId: string,
): Promise<string | null> => {
  if (!session?.accessToken || !session?.clinicId || !patientId) {
    return null;
  }

  const requestKey = getRequestKey(session.clinicId, patientId);
  if (inFlightProfileImageRequests.has(requestKey)) {
    return inFlightProfileImageRequests.get(requestKey)!;
  }

  const request = getPatientImages(session, patientId, {
    viewTypes: [...PROFILE_VIEW_TYPE_PRIORITY],
  })
    .then((images) => pickBestProfileImageUrl(images))
    .catch((error) => {
      console.warn(`Failed to fetch profile image for patient ${patientId}`, {
        message: error instanceof Error ? error.message : "Unknown error",
      });
      return null;
    })
    .finally(() => {
      inFlightProfileImageRequests.delete(requestKey);
    });

  inFlightProfileImageRequests.set(requestKey, request);
  return request;
};

export const enrichPatientsWithProfileImages = async <
  T extends PatientWithProfileImage,
>(
  patients: T[],
  session: TSession | null,
): Promise<T[]> => {
  if (!Array.isArray(patients) || patients.length === 0) {
    return patients;
  }

  if (!session?.accessToken || !session?.clinicId) {
    return patients.map((patient) => ({
      ...patient,
      profileImageUrl: patient.profileImageUrl || null,
    }));
  }

  const patientIds = [
    ...new Set(
      patients
        .map((patient) => patient.patientRecordId || patient.clinicOSUUID)
        .filter((patientId): patientId is string => Boolean(patientId)),
    ),
  ];

  if (patientIds.length === 0) {
    return patients.map((patient) => ({
      ...patient,
      profileImageUrl: patient.profileImageUrl || null,
    }));
  }

  try {
    // Use the batch endpoint to fetch all patient images in one request
    const imagesByPatient = await getMultiplePatientsImages(
      session,
      patientIds,
      {
        viewTypes: [...PROFILE_VIEW_TYPE_PRIORITY],
      },
    );

    // Create a map of patient IDs to their best profile image URL
    const imageMap = new Map<string, string | null>();
    for (const [patientId, images] of Object.entries(imagesByPatient)) {
      imageMap.set(patientId, pickBestProfileImageUrl(images));
    }

    // For patients without images in the response, set to null
    patientIds.forEach((patientId) => {
      if (!imageMap.has(patientId)) {
        imageMap.set(patientId, null);
      }
    });

    return patients.map((patient) => {
      const patientId = patient.patientRecordId || patient.clinicOSUUID;
      return {
        ...patient,
        profileImageUrl: patientId ? imageMap.get(patientId) || null : null,
      };
    });
  } catch (error) {
    console.warn("Failed to batch fetch profile images, falling back", {
      message: error instanceof Error ? error.message : "Unknown error",
    });
    // Return patients without profile images on error
    return patients.map((patient) => ({
      ...patient,
      profileImageUrl: patient.profileImageUrl || null,
    }));
  }
};
